import { memo } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import SectionHeader from "src-next/shared/ui/sections/SectionHeader";

export interface CrmStatisticsSectionProps {
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
  dragHandleProps?: {
    attributes?: object;
    listeners?: object;
  };
}

const MOCKED_KANBAN_COLUMNS = [
  {
    id: "triagePreConsultation",
    title: "Triagem / Pré-consulta",
    patients: [
      { id: "patient-1", name: "Ana Paula Ribeiro", context: "Questionário enviado" },
      { id: "patient-2", name: "Pedro Henrique Nunes", context: "Aguardando resposta" },
      { id: "patient-3", name: "Marina Costa Silva", context: "Lembrete agendado para hoje" },
    ],
  },
  {
    id: "confirmationPayment",
    title: "Confirmação / Pagamento",
    patients: [
      { id: "patient-4", name: "Luiz Gustavo Ferreira", context: "Pagamento pendente" },
      { id: "patient-5", name: "Carla Menezes Gomes", context: "Consulta confirmada" },
    ],
  },
  {
    id: "postConsultation",
    title: "Pós-consulta",
    patients: [
      { id: "patient-6", name: "Joana Martins Rocha", context: "Follow-up em 7 dias" },
      { id: "patient-7", name: "Ricardo Alves Santos", context: "Pesquisa de satisfação enviada" },
    ],
  },
] as const;

const CrmStatisticsSection = memo<CrmStatisticsSectionProps>(
  ({ isCollapsed = false, onToggleCollapsed, dragHandleProps }) => {
    return (
      <Box>
        <SectionHeader
          icon={<QueryStatsIcon fontSize="small" />}
          title="Jornada de pacientes"
          subtitle="Painel kanban da jornada clínica com foco em quais pacientes estão em cada etapa."
          isCollapsed={isCollapsed}
          onCollapsedChange={onToggleCollapsed}
          actions={
            <IconButton
              size="small"
              aria-label="Reordenar seção"
              sx={{ cursor: "grab" }}
              {...(dragHandleProps?.attributes ?? {})}
              {...(dragHandleProps?.listeners ?? {})}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          }
        >
          <Box
            sx={{
              mt: 1.5,
              p: { xs: 1.25, sm: 1.5 },
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                {MOCKED_KANBAN_COLUMNS.map((kanbanColumn) => (
                  <Paper key={kanbanColumn.id} variant="outlined" sx={{ p: 1.5, flex: 1 }}>
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight={700}>
                          {kanbanColumn.title}
                        </Typography>
                        <Chip
                          size="small"
                          color="primary"
                          icon={<PeopleAltIcon />}
                          label={kanbanColumn.patients.length}
                        />
                      </Stack>

                      {kanbanColumn.patients.map((patient) => (
                        <Paper key={patient.id} variant="outlined" sx={{ p: 1.25 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {patient.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {patient.context}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Stack>
          </Box>
        </SectionHeader>
      </Box>
    );
  },
);

CrmStatisticsSection.displayName = "CrmStatisticsSection";

export default CrmStatisticsSection;
